import { useCallback, useEffect, useState } from "react";
import { isTaskMantisDesktop } from "../../desktopUpdate";
import { CAPTURE_HOTKEY_LABEL } from "./captureHotkeyTip";

/** Quick capture is off until the user opts in, so this is the pre-load shape too. */
export const DEFAULT_CAPTURE_HOTKEY_STATE = {
  enabled: false,
  decided: true,
  hotkey: CAPTURE_HOTKEY_LABEL,
  accessibilityTrusted: false,
  hotkeyRegistered: false,
};

/** Normalize an IPC payload into the full state shape (missing fields default off). */
export const normalizeCaptureHotkeyState = (raw) => ({
  enabled: raw?.enabled === true,
  decided: raw?.decided === true,
  hotkey: String(raw?.hotkey || CAPTURE_HOTKEY_LABEL),
  accessibilityTrusted: raw?.accessibilityTrusted === true,
  hotkeyRegistered: raw?.hotkeyRegistered === true,
});

/**
 * Read + flip the desktop quick-capture opt-in. `loading` stays true in the
 * browser, where there is nothing to opt into.
 *
 * Enabling triggers the macOS Accessibility prompt in the main process, so
 * `setEnabled` must only ever be called from a user action.
 *
 * @example
 * const { state, setEnabled } = useCaptureHotkeyState();
 * await setEnabled(true);
 * // state.enabled === true; state.accessibilityTrusted reflects the OS answer
 */
export const useCaptureHotkeyState = () => {
  const [state, setState] = useState(DEFAULT_CAPTURE_HOTKEY_STATE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isTaskMantisDesktop()) return undefined;
    let cancelled = false;
    window.starmeshDesktop
      .getCaptureHotkeyState?.()
      ?.then?.((next) => {
        if (cancelled) return;
        setState(normalizeCaptureHotkeyState(next));
        setLoading(false);
      })
      ?.catch?.(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setEnabled = useCallback(async (enabled) => {
    setBusy(true);
    try {
      const next = await window.starmeshDesktop?.setCaptureHotkeyEnabled?.(enabled);
      const normalized = normalizeCaptureHotkeyState(next);
      setState(normalized);
      return normalized;
    } finally {
      setBusy(false);
    }
  }, []);

  const openAccessibilitySettings = useCallback(() => {
    void window.starmeshDesktop?.openAccessibilitySettings?.();
  }, []);

  return { state, loading, busy, setEnabled, openAccessibilitySettings };
};

export default useCaptureHotkeyState;
